import { memo, useCallback } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import { FILTER_CHIP_HEIGHTS } from 'LuxKit/Chips/FilterChip'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntl } from 'react-intl'
import { capitalise } from 'lib/string/stringUtils'
import { trackClientEvent } from 'analytics/analytics'
import { chatExploreCategories, type ChatExploreCategory } from 'tripPlanner/config/exploreCategories'
import ExploreCategoryChip from './ExploreCategoryChip'
import ExploreChipCarousel from './ExploreChipCarousel'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import focusStyle from 'styles/utils/focusStyle'

interface Props {
  /** The search query to display as the filter label */
  hotelSearchQuery: string
  /** Whether the hotel filter is visible */
  isHotelFilterVisible: boolean
  /** Called when the hotel filter is dismissed */
  onHotelFilterDismiss: () => void
  /** The experience search query to display as the filter label */
  experienceSearchQuery: string
  /** Whether the experience filter is visible */
  isExperienceFilterVisible: boolean
  /** Called when the experience filter is dismissed */
  onExperienceFilterDismiss: () => void
  /** Whether the explore filters are visible */
  isExploreFiltersVisible: boolean
  /** Current explore category selection */
  selectedExploreCategory?: ChatExploreCategory | undefined
  /** Called when explore category changes */
  onExploreCategorySelect: (category: ChatExploreCategory | undefined) => void
  /** Whether explore data is loading */
  isExploreFetching: boolean
}

const OverlayFilterChip = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  max-width: 100%;
  min-width: 0;
  height: ${rem(FILTER_CHIP_HEIGHTS.medium)};
  padding-inline: ${rem(12)};
  border: none;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  box-shadow: inset 0 0 0 ${rem(1)} ${props => props.theme.palette.neutral.surface.darkForeground};
  cursor: pointer;
  text-align: start;

  &:focus-visible {
    ${focusStyle}
  }

  > svg {
    flex-shrink: 0;
    width: ${rem(16)};
    height: ${rem(16)};
  }
`

const OverlayFilterChipLabel = styled(BodyText)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * ChatMapFilters displays dynamic filter chips above the map.
 * Supports hotel search and explore category filters.
 */
function ChatMapFilters({
  hotelSearchQuery,
  isHotelFilterVisible,
  onHotelFilterDismiss,
  experienceSearchQuery,
  isExperienceFilterVisible,
  onExperienceFilterDismiss,
  isExploreFiltersVisible,
  selectedExploreCategory,
  onExploreCategorySelect,
  isExploreFetching,
}: Props) {
  const intl = useIntl()
  const handleExploreCategorySelect = useCallback((category: ChatExploreCategory) => {
    const isDeselect = selectedExploreCategory?.key === category.key

    trackClientEvent({
      subject: 'chat_map_explore_filter',
      action: isDeselect ? 'deselect' : `select_${category.key.replace(/-/g, '_')}`,
      category: 'chat_trip_planner',
      type: 'interaction',
    })

    if (isDeselect) {
      onExploreCategorySelect(undefined)
      return
    }
    onExploreCategorySelect(category)
  }, [onExploreCategorySelect, selectedExploreCategory])

  const handleHotelFilterDismiss = useCallback(() => {
    trackClientEvent({
      subject: 'chat_map_hotel_filter',
      action: 'dismiss',
      category: 'chat_trip_planner',
      type: 'interaction',
    })
    onHotelFilterDismiss()
  }, [onHotelFilterDismiss])

  const handleExperienceFilterDismiss = useCallback(() => {
    trackClientEvent({
      subject: 'chat_map_experience_filter',
      action: 'dismiss',
      category: 'chat_trip_planner',
      type: 'interaction',
    })
    onExperienceFilterDismiss()
  }, [onExperienceFilterDismiss])

  const showHotelFilter = isHotelFilterVisible && Boolean(hotelSearchQuery)
  const showExperienceFilter = isExperienceFilterVisible && Boolean(experienceSearchQuery)
  const showExploreFilters = isExploreFiltersVisible

  if (!showHotelFilter && !showExperienceFilter && !showExploreFilters) {
    return null
  }

  let hotelFilterLabel: string | undefined
  if (showHotelFilter) {
    hotelFilterLabel = intl.formatMessage({
      id: 'ep7MfYGZ4XhDNlmp7H7H',
      defaultMessage: 'Hotels | {query}',
      description: 'Hotel filter chip label showing the search query on the chat map',
    }, { query: capitalise(hotelSearchQuery) })
  }

  let experienceFilterLabel: string | undefined
  if (showExperienceFilter) {
    experienceFilterLabel = intl.formatMessage({
      id: 'B3Pa_hXN4rBikMx1F4l6',
      defaultMessage: 'Experiences | {query}',
      description: 'Experience filter chip label showing the search query on the chat map',
    }, { query: capitalise(experienceSearchQuery) })
  }

  return (
    <Group direction="vertical" gap={8} fullWidth>
      {hotelFilterLabel && (
        <Group direction="horizontal" fullWidth>
          <OverlayFilterChip onClick={handleHotelFilterDismiss}>
            <LineTimesIcon size="XS" colour="dark-contrast" />
            <OverlayFilterChipLabel variant="medium">
              {hotelFilterLabel}
            </OverlayFilterChipLabel>
          </OverlayFilterChip>
        </Group>
      )}
      {experienceFilterLabel && (
        <Group direction="horizontal" fullWidth>
          <OverlayFilterChip onClick={handleExperienceFilterDismiss}>
            <LineTimesIcon size="XS" colour="dark-contrast" />
            <OverlayFilterChipLabel variant="medium">
              {experienceFilterLabel}
            </OverlayFilterChipLabel>
          </OverlayFilterChip>
        </Group>
      )}
      {showExploreFilters && (
        <ExploreChipCarousel gutterSize={24} desktopGutterSize={40}>
          {chatExploreCategories.map((category) => (
            <ExploreCategoryChip
              key={category.key}
              category={category}
              isSelected={selectedExploreCategory?.key === category.key}
              isDisabled={isExploreFetching}
              onSelect={handleExploreCategorySelect}
            />
          ))}
        </ExploreChipCarousel>
      )}
    </Group>
  )
}

export default memo(ChatMapFilters)
